import type { MapTypeStyle } from 'google-map-react';
import type GoogleMapReact from 'google-map-react';
import type { Coordinates } from '@volkswagen-onehub/audi-feature-service-visitor-locator';

export const DEFAULT_CENTER_COORDINATES: Coordinates = {
  lat: 51.165691,
  lng: 10.451526,
};

export const DEFAULT_ZOOM = 6;

export const DEFAULT_ZOOM_ON_DEALER = 13;

export const DEFAULT_Y_OFFSET = 0.002;

export const DEFAULT_BOUNDS: GoogleMapReact.Bounds = {
  nw: {
    lat: 0,
    lng: 0,
  },
  ne: {
    lat: 0,
    lng: 0,
  },
  sw: {
    lat: 0,
    lng: 0,
  },
  se: {
    lat: 0,
    lng: 0,
  },
};

export const getMapOptions = (isMobile: boolean) => ({
  fullscreenControl: false,
  zoomControl: !isMobile,
  gestureHandling: 'greedy',
  styles: process.env.IS_LEGACY
    ? ('' as unknown as MapTypeStyle[])
    : (DARK_MODE as unknown as MapTypeStyle[]),
});

export const getBootstrapURLKeys = (marketGoogleKey: string, language: string) => ({
  key: marketGoogleKey,
  libraries: ['places', 'marker'],
  language,
});

declare global {
  interface Window {
    vtpGoogleMapsPromise?: Promise<typeof google>;
    vtpInitMap?: () => void;
  }
}

const GOOGLE_MAPS_SCRIPT_SELECTOR =
  'script[src*="maps.googleapis.com/maps/api/js"], script[src*="maps.gstatic.com/maps"]';
const GOOGLE_MAPS_LOAD_TIMEOUT_MS = 10000;

type OptionalGoogleMapsWindow = Omit<Window, 'google'> & {
  google?: typeof google;
};

const getGoogleMapsWindow = (): OptionalGoogleMapsWindow => window;

const getUsableGoogleMapsApi = (googleMapsWindow: OptionalGoogleMapsWindow) => {
  const googleMaps = googleMapsWindow.google as
    | { maps?: { LatLng?: unknown; Map?: unknown } }
    | undefined;
  if (!googleMaps?.maps?.Map || !googleMaps.maps.LatLng) return undefined;
  return googleMapsWindow.google;
};

export const isGoogleMapsLoaded = () =>
  typeof window !== 'undefined' && !!getUsableGoogleMapsApi(getGoogleMapsWindow());

const waitForGoogleMaps = (
  resolve: (googleMaps: typeof google) => void,
  reject: (reason?: unknown) => void,
) => {
  const googleMapsWindow = getGoogleMapsWindow();
  const timeout = window.setTimeout(() => {
    window.clearInterval(interval);
    reject(new Error('Timed out waiting for Google Maps API to load'));
  }, GOOGLE_MAPS_LOAD_TIMEOUT_MS);

  const resolveGoogleMaps = () => {
    const googleMapsApi = getUsableGoogleMapsApi(googleMapsWindow);
    if (googleMapsApi) {
      window.clearTimeout(timeout);
      window.clearInterval(interval);
      resolve(googleMapsApi);
    }
  };

  const interval = window.setInterval(resolveGoogleMaps, 50);
  resolveGoogleMaps();

  return resolveGoogleMaps;
};

export const loadGoogleMaps = (
  marketGoogleKey: string,
  language: string,
): Promise<typeof google> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps API can only be loaded in the browser'));
  }

  const googleMapsWindow = getGoogleMapsWindow();

  const googleMapsApi = getUsableGoogleMapsApi(googleMapsWindow);
  if (googleMapsApi) {
    return Promise.resolve(googleMapsApi);
  }

  if (window.vtpGoogleMapsPromise) {
    return window.vtpGoogleMapsPromise;
  }

  const existingInitMap = window.vtpInitMap;

  window.vtpGoogleMapsPromise = new Promise<typeof google>((resolve, reject) => {
    const resolveGoogleMaps = waitForGoogleMaps(resolve, reject);

    const existingScript = document.querySelector<HTMLScriptElement>(GOOGLE_MAPS_SCRIPT_SELECTOR);
    if (existingScript) {
      existingScript.addEventListener('load', resolveGoogleMaps, { once: true });
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    window.vtpInitMap = () => {
      existingInitMap?.();
      resolveGoogleMaps();
    };

    const script = document.createElement('script');
    const params = new URLSearchParams({
      key: marketGoogleKey,
      loading: 'async',
      libraries: getBootstrapURLKeys(marketGoogleKey, language).libraries.join(','),
      callback: 'vtpInitMap',
    });

    if (language) {
      params.set('language', language);
    }

    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.onerror = reject;

    document.head.appendChild(script);
  });

  return window.vtpGoogleMapsPromise;
};

export const DARK_MODE = [
  { elementType: 'geometry', stylers: [{ color: '#1d2c4d' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8ec3b9' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a3646' }] },
  {
    featureType: 'administrative.country',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#4b6878' }],
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#64779e' }],
  },
  {
    featureType: 'administrative.province',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#4b6878' }],
  },
  {
    featureType: 'landscape.man_made',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#334e87' }],
  },
  {
    featureType: 'landscape.natural',
    elementType: 'geometry',
    stylers: [{ color: '#023e58' }],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: '#283d6a' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6f9ba5' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#1d2c4d' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry.fill',
    stylers: [{ color: '#023e58' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#3C7680' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#304a7d' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#98a5be' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#1d2c4d' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#2c6675' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#255763' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#b0d5ce' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#023e58' }],
  },
  {
    featureType: 'transit',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#98a5be' }],
  },
  {
    featureType: 'transit',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#1d2c4d' }],
  },
  {
    featureType: 'transit.line',
    elementType: 'geometry.fill',
    stylers: [{ color: '#283d6a' }],
  },
  {
    featureType: 'transit.station',
    elementType: 'geometry',
    stylers: [{ color: '#3a4762' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#0e1626' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#4e6d70' }],
  },
];
